import { InjectionKey } from 'vue';
import { RepositoryFactory, Repositories } from '@/repositories/RepositoryFactory';
import {
  CustomerHistory,
  CustomerStamp,
  CustomerStampCardParsed,
  StampCardGoal,
  StampCardParsed,
  StampCardSettings,
} from '@comsbi/common/src/types/models';

export { StampCardSettings, StampCardParsed, CustomerStampCardParsed };

export type CustomerStampCard = Omit<CustomerStampCardParsed, 'stampCard'> & { stampCard: StampCardParsed };

const StampCardRepository = RepositoryFactory.get('stampCard') as Repositories['stampCard'];

const CHANNEL_ID: string =
  process.env.NODE_ENV === 'local' ? window.CHANNEL_ID || process.env.VUE_APP_CHANNEL_ID : window.CHANNEL_ID;

export const useStampCard = () => {
  /**
   * 指定スタンプカードマスターの顧客データを生成取得する
   */
  const resolve = async (stampCardId: string): Promise<{ result: CustomerStampCard }> => {
    return (await StampCardRepository.resolveCustomerStampCard(stampCardId)) as any;
  };

  /**
   * スタンプ行動履歴を取得
   */
  const getHistory = async (channelId: string, stampCardId: string, from = 0, limit = 50): Promise<any[]> => {
    return (await StampCardRepository.getCustomerDataStampCard(channelId, stampCardId, from, limit)) as any;
  };

  const stamp = async (
    stampCardId: string,
    itemKey?: string,
    lat: number | null = null,
    lng: number | null = null
  ): Promise<{ result: { customerStamp: CustomerStampCard; goal?: StampCardGoal } }> => {
    return (await StampCardRepository.stamp(stampCardId, itemKey, lat, lng)) as any;
  };

  /**
   * 直近のスタンプ取得時間を取得
   */
  const getLatestTimestamp = (stamps: CustomerStamp[]): number | undefined => {
    if (!stamps.length) return undefined;
    return new Date(stamps[stamps.length - 1].stampedAt).getTime();
  };

  /**
   * 現在のラウンドの最初のスタンプ時間を取得
   */
  const getFirstStampTimestamp = (stamps: CustomerStamp[]): number | undefined => {
    if (!stamps.length) return undefined;
    return new Date(stamps[0].stampedAt).getTime();
  };

  /**
   * 対象のスタンプ取得からN日経過しているか判定
   */
  const isPastDays = (timestamp: number, termDays: number) => {
    const latestStampDate = new Date(timestamp);
    const today = new Date();

    const targetDate = new Date(latestStampDate.getTime() + termDays * 24 * 60 * 60 * 1000);
    return today > targetDate;
  };

  /**
   * スタンプカードのスタンプをリセット
   * リセットしたスタンプたちをhistoriesに格納
   * @param {string} customerStampCardId
   * @param {boolean} continueWithStamps 現在の周のスタンプを維持するor全てリセット
   */
  const resetCustomerStamps = async (
    customerStampCardId: string,
    continueWithStamps: boolean
  ): Promise<{ status: number; message: string; data: any }> => {
    const result = (await StampCardRepository.resetCustomerStamps(customerStampCardId, continueWithStamps)) as any;
    return result;
  };

  return {
    resolve,
    getHistory,
    stamp,
    resetCustomerStamps,

    getLatestTimestamp,
    getFirstStampTimestamp,
    isPastDays,
  };
};

type StampCardType = ReturnType<typeof useStampCard>;
export const StampCardKey: InjectionKey<StampCardType> = Symbol('StampCard');
